import type { TagVariant } from "../../../../../design-system";

// Honest state labels. Schedule, Delivery and Publication are separate
// dimensions and are never merged. A handoff (email accepted or delivered,
// TikTok inbox) is never labelled as posted: only an owner report or a
// provider verification is publication.

export type ChipSpec = { label: string; variant: TagVariant };

export const scheduleChip = (state: string): ChipSpec => {
  switch (state) {
    case "unscheduled":
      return { label: "Not scheduled", variant: "neutral" };
    case "scheduled":
      return { label: "Scheduled", variant: "info" };
    case "dispatching":
      return { label: "Sending now", variant: "warning" };
    case "completed":
      return { label: "Handoff finished", variant: "success" };
    case "cancelled":
      return { label: "Cancelled", variant: "neutral" };
    case "cancellation_requested":
      return { label: "Cancel requested", variant: "warning" };
    case "blocked":
      return { label: "Blocked", variant: "danger" };
    default:
      return { label: state || "Unknown", variant: "neutral" };
  }
};

export const deliveryChip = (state: string, mode: string): ChipSpec => {
  const tiktok = mode === "tiktok_inbox";
  switch (state) {
    case "pending":
      return { label: "Not sent yet", variant: "neutral" };
    case "processing":
      return { label: "Sending", variant: "warning" };
    case "accepted":
      return tiktok
        ? { label: "Accepted by TikTok, not published", variant: "info" }
        : mode === "auto_publish"
        ? { label: "Accepted by provider, not verified", variant: "info" }
        : { label: "Email accepted, not published", variant: "info" };
    case "handed_off":
      return tiktok
        ? { label: "In TikTok inbox, not published", variant: "info" }
        : { label: "Email delivered, not published", variant: "info" };
    case "failed":
      return { label: "Delivery failed", variant: "danger" };
    case "unknown":
      return { label: "Outcome unknown, needs reconcile", variant: "danger" };
    default:
      return { label: state || "Unknown", variant: "neutral" };
  }
};

/** Who reported a publication: the stored source, else derived from the stored actor kind. */
export const publicationSource = (evidence?: { source?: string | null; actor?: { kind?: string } | null } | null): string => {
  if (evidence?.source) return evidence.source;
  const kind = evidence?.actor?.kind;
  if (kind === "owner_link") return "owner";
  if (kind === "admin") return "admin";
  if (kind === "script" || kind === "worker") return "script";
  return kind ? "mcp" : "";
};

/** Honest "marked as posted" wording, by who reported it (owner link, admin, MCP). */
export const markedPostedLabel = (source?: string | null): string => {
  switch (source) {
    case "owner":
      return "Marked as posted by the owner";
    case "admin":
      return "Marked as posted by an admin";
    case "mcp":
      return "Marked as posted through an AI assistant";
    case "script":
      return "Marked as posted by an automated process";
    default:
      return "Marked as posted (reporter not recorded)";
  }
};

export const publicationChip = (state: string, source?: string | null): ChipSpec => {
  switch (state) {
    case "unconfirmed":
      return { label: "Not confirmed as posted", variant: "neutral" };
    case "owner_reported":
      return { label: markedPostedLabel(source), variant: "success" };
    case "provider_verified":
      return { label: "Posted, verified by provider", variant: "success" };
    case "failed":
      return { label: "Publication failed", variant: "danger" };
    default:
      return { label: state || "Unknown", variant: "neutral" };
  }
};

export const jobChip = (status: string): ChipSpec => {
  switch (status) {
    case "queued":
      return { label: "Queued", variant: "neutral" };
    case "running":
      return { label: "Running", variant: "info" };
    case "succeeded":
      return { label: "Succeeded", variant: "success" };
    case "failed":
      return { label: "Failed", variant: "danger" };
    case "cancelled":
      return { label: "Cancelled", variant: "neutral" };
    default:
      return { label: status || "Unknown", variant: "neutral" };
  }
};

export const assetChip = (status: string): ChipSpec => {
  switch (status) {
    case "eligible":
      return { label: "Eligible", variant: "success" };
    case "quarantined":
      return { label: "Quarantined", variant: "warning" };
    case "rejected":
      return { label: "Rejected", variant: "danger" };
    default:
      return { label: status || "Unknown", variant: "neutral" };
  }
};

export const modeLabel = (mode: string | null | undefined): string => {
  switch (mode) {
    case "owner_email":
      return "Email me to publish";
    case "tiktok_inbox":
      return "TikTok inbox";
    case "auto_publish":
      return "Publish directly";
    default:
      return mode || "none";
  }
};

export const severityVariant = (severity: string): TagVariant => {
  const s = (severity || "").toLowerCase();
  if (s === "critical" || s === "error" || s === "high") return "danger";
  if (s === "warning" || s === "warn" || s === "medium") return "warning";
  if (s === "ok" || s === "healthy") return "success";
  return "info";
};
